import React, { useId, useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { ChevronDown, HelpCircle, Info } from 'lucide-react';
import { Link } from 'react-router-dom';
import { demoGraphs } from '../../data/demoGraphs';
import { dashboardPanelFeatured } from './dashboardStyles';
import { DashboardIcon } from './DashboardIcon';
import { JargonText } from '../demoJargon';
import { workbenchHrefForDemo } from './demoCatalog';
import { diagramPalette } from '../../constants/diagramPalette';

type Props = {
    /** When Start here is dismissed, legend starts collapsed */
    defaultExpanded?: boolean;
};

type LegendShape = 'round-rectangle' | 'diamond' | 'triangle' | 'octagon' | 'contract' | 'critical';

/** Dashboard legend shapes / risk / borders — Step 1 taxonomy. Mixer uses dashed blue (contract-border family) for the swatch. */
const LEGEND_ITEMS: ReadonlyArray<{ shape: LegendShape; fill: string; stroke: string; strokeStyle?: 'solid' | 'dashed'; label: string }> = [
    { shape: 'round-rectangle', fill: '#e2e8f0', stroke: '#64748b', label: 'Address (default round-rect)' },
    { shape: 'diamond', fill: '#e0f2fe', stroke: '#2563eb', strokeStyle: 'dashed', label: 'Mixer (diamond)' },
    { shape: 'triangle', fill: '#fecaca', stroke: '#ef4444', label: 'Scam / mule (triangle)' },
    { shape: 'triangle', fill: diagramPalette.mule.fill, stroke: diagramPalette.mule.stroke, label: 'Mule wallet (triangle)' },
    { shape: 'contract', fill: '#e0f2fe', stroke: '#2563eb', strokeStyle: 'dashed', label: 'Smart contract — dashed blue border line' },
    { shape: 'octagon', fill: '#fbcfe8', stroke: '#ec4899', strokeStyle: 'dashed', label: 'Attribution hub (pink octagon)' },
    { shape: 'critical', fill: '#fecaca', stroke: '#ef4444', label: 'Critical risk (red fill tint)' },
];

const focusRing =
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

const ENTITY_TAXONOMY_DEMO_ID = 'entity-taxonomy-showcase';

const ShapeSwatch: React.FC<{
    shape: LegendShape;
    fill: string;
    stroke: string;
    strokeStyle?: 'solid' | 'dashed';
}> = ({ shape, fill, stroke, strokeStyle = 'solid' }) => {
    const s = 22;
    const cx = s / 2;
    const cy = s / 2;
    const pad = 2.5;
    const r = s / 2 - pad;
    const dash = strokeStyle === 'dashed' ? '3,2.5' : undefined;
    const sw = 2.4;

    let glyph: React.ReactNode;
    if (shape === 'diamond') {
        glyph = (
            <polygon
                points={`${cx},${pad} ${s - pad},${cy} ${cx},${s - pad} ${pad},${cy}`}
                fill={fill}
                stroke={stroke}
                strokeWidth={sw}
                strokeDasharray={dash}
                strokeLinejoin="round"
            />
        );
    } else if (shape === 'triangle') {
        glyph = (
            <polygon
                points={`${cx},${pad} ${s - pad},${s - pad} ${pad},${s - pad}`}
                fill={fill}
                stroke={stroke}
                strokeWidth={sw}
                strokeLinejoin="round"
            />
        );
    } else if (shape === 'octagon') {
        const pts = Array.from({ length: 8 }, (_, i) => {
            const a = (Math.PI / 4) * i - Math.PI / 8;
            return `${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`;
        }).join(' ');
        glyph = (
            <polygon
                points={pts}
                fill={fill}
                stroke={stroke}
                strokeWidth={sw}
                strokeDasharray={dash}
                strokeLinejoin="round"
            />
        );
    } else {
        // round-rectangle / contract / critical — same silhouette, different stroke/fill
        glyph = (
            <rect
                x={pad}
                y={pad + 1}
                width={s - pad * 2}
                height={s - pad * 2 - 2}
                rx="3.5"
                fill={fill}
                stroke={stroke}
                strokeWidth={sw}
                strokeDasharray={dash}
            />
        );
    }

    return (
        <div
            className="flex items-center justify-center w-9 h-9 shrink-0 rounded-md bg-white dark:bg-slate-900 border border-slate-300/80 dark:border-slate-600 shadow-sm"
            aria-hidden
        >
            <svg width={s} height={s}>{glyph}</svg>
        </div>
    );
};

export const GraphLegend: React.FC<Props> = ({ defaultExpanded = true }) => {
    const [expanded, setExpanded] = useState(defaultExpanded);
    const panelId = useId();
    const entityTaxonomyDemoHref = useMemo(() => {
        const demo = demoGraphs[ENTITY_TAXONOMY_DEMO_ID];
        if (!demo) return '/workbench';
        return workbenchHrefForDemo({
            demo: demo.id,
            chain: demo.realChain || demo.chain || 'ETH',
            address: demo.realAddress || demo.address || '',
        });
    }, []);

    return (
        <div className={clsx('overflow-hidden', dashboardPanelFeatured)}>
            <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                aria-expanded={expanded}
                aria-controls={panelId}
                aria-label={expanded ? 'Collapse graph legend' : 'Expand graph legend'}
                className={clsx(
                    'w-full flex items-center justify-between gap-3 px-4 py-3.5 min-h-11 text-left',
                    'hover:bg-brand-cream/40 dark:hover:bg-primary/5 active:bg-muted/50 transition-colors',
                    focusRing,
                )}
            >
                <div className="flex items-start gap-2.5 min-w-0">
                    <span className="mt-0.5 p-1.5 rounded-md bg-primary/10 shrink-0">
                        <HelpCircle className="w-3.5 h-3.5 text-primary" aria-hidden />
                    </span>
                    <div className="min-w-0">
                        <h3 className="font-semibold text-sm text-foreground">Graph legend</h3>
                        <p className="text-xs text-muted-foreground mt-0.5">
                            Covered in Step 1 — Entity Taxonomy
                        </p>
                    </div>
                </div>
                <DashboardIcon
                    variant="inline"
                    tone="neutral"
                    icon={ChevronDown}
                    className={clsx(
                        'transition-transform duration-200 ease-out shrink-0',
                        expanded ? 'rotate-0' : '-rotate-90',
                    )}
                />
            </button>
            <div
                id={panelId}
                className={clsx(
                    'grid transition-[grid-template-rows] duration-200 ease-out',
                    expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
                )}
            >
                <div className="overflow-hidden">
                    <div className="px-4 pb-4 flex flex-wrap gap-x-5 gap-y-3 text-xs border-t border-border/60 pt-3 bg-brand-cream/20 dark:bg-primary/[0.03]">
                        {LEGEND_ITEMS.map((item) => (
                            <div key={item.label} className="flex items-center gap-2.5 min-h-9">
                                <ShapeSwatch
                                    shape={item.shape}
                                    fill={item.fill}
                                    stroke={item.stroke}
                                    strokeStyle={item.strokeStyle}
                                />
                                <span className="text-foreground font-medium leading-snug">{item.label}</span>
                            </div>
                        ))}
                    </div>
                    <p className="px-4 pb-3 text-[10px] text-muted-foreground leading-relaxed">
                        <JargonText text="Subcategory details (hot wallet, deposit, OTC…) appear as chips in the Node Inspector when you click a node — not as coloured borders." />
                        {' '}
                        <span className="text-muted-foreground/70">Hover dotted terms for a short explainer.</span>
                    </p>
                    <div className="px-4 pb-4 pt-0">
                        <Link
                            to={entityTaxonomyDemoHref}
                            data-testid="graph-legend-view-demo"
                            className={clsx(
                                'flex items-center justify-center gap-1.5 w-full py-2 px-3 rounded-lg',
                                'text-xs font-medium text-foreground',
                                'border border-border bg-card hover:bg-muted/60 transition-colors',
                                focusRing,
                            )}
                        >
                            <Info className="w-3.5 h-3.5 text-muted-foreground shrink-0" aria-hidden />
                            View Demo
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};
